import { getLanguage, reactive } from 'cdui-js';
import { fetchBusConfigs, fetchCountry } from '../api/common';

/** 字典原文仅来自 Theme / API，无本地默认数据 */
let sourceData: Record<string, any[]> = {};

/** busConfigs 非 reactive；灌入/语言刷新后递增，供页面 watch 重建选项 */
export const busConfigsTick = reactive({ v: 0 });
const bumpBusConfigsTick = () => {
  busConfigsTick.v += 1;
};

const langMap = {
  'zh-CN': 'scDesc',
  'zh-TW': 'tcDesc',
  en: 'enDesc',
} as const;

type BusLang = keyof typeof langMap;

/** 每次调用时解析，避免模块加载时语言被写死；可显式传入（切语言时） */
const resolveBusLang = (raw?: string | null): BusLang => {
  const lang = String(raw || getLanguage() || 'zh-CN').trim();
  if (lang === 'en' || lang === 'en-US' || lang.startsWith('en-')) return 'en';
  if (lang === 'zh-CN' || lang === 'zh-Hans' || lang === 'zh') return 'zh-CN';
  if (lang === 'zh-TW' || lang === 'zh-HK' || lang === 'zh-Hant' || lang.includes('zh-')) return 'zh-TW';
  if (lang in langMap) return lang as BusLang;
  return 'zh-CN';
};

export let busConfigs = {
  // 身份证类型
  identityType: [],
  // 性别
  gender: [],
  // 学历
  education: [],
  // 受雇状态
  employmentStatus: [],
  // 行业
  industry: [],
  // 职位
  position: [],
  // 受雇年限
  employmentYear: [],
  // 收入来源
  incomeSource: [],
  // 资产类别
  assetCategory: [],
  // 总资产净值
  totalAssetNetWorth: [],
  // 每年总收入
  annualTotalIncome: [],
  // 物业情况
  housingProperty: [],
  // 财富来源 / 资金来源（表单 wealthSource、sourceOfFunds 共用）
  sourceOfFunds: [],
  // 资金转入方式
  fundTransferMethod: [],
  // 资金来源地
  sourceOfFundsLocation: [],
  // 投资目标
  investmentObjective: [],
  // 投资知识
  investmentKnowledge: [],
  // 投资经验类型
  investmentExperienceType: [],
  // 经验（年资）
  investmentExperience: [],
  // 基金账户
  fundAccountType: [],
  // 证券账户
  securitiesAccountType: [],
  // 交易权限
  tradePermission: [],
  // 香港银行列表
  hongKongBank: [],
  // 账户类型
  bankAccountType: [],
  // 开户方式
  openAccountMethod: [],
  // 币种
  depositCurrency: [],
  //
  electronicService: [],

  countryList: [],

  orderType: [],

  // 币种
  currencyType: [],
};

export let orignCountryList = {};

/** App 已通过 Theme 下发 country（含 {}），跳过 /sys/dict/country */
let countryFromApp = false;

/** App 已通过 Theme 下发 busConfigs，跳过 /sys/dict/busConfigs */
let busConfigsFromApp = false;

/** 已成功拉过字典则跳过；并发调用复用同一 Promise，避免 country/busConfigs 重复请求 */
let busConfigsInited = false;
let initBusConfigsPromise: Promise<{ data: typeof busConfigs }> | null = null;

const mapCountryRows = (cdata: any, lang?: string | null) => {
  const langKey = langMap[resolveBusLang(lang)];
  const countryList: { label: string; value: string; other: string }[] = [];
  Object.values(cdata || {}).forEach((item: any) => {
    if (!Array.isArray(item)) return;
    item.forEach((r: any) => {
      const label = r?.[langKey] ?? '';
      r.label = label;
      r.value = r.code;
      countryList.push({
        label,
        value: r.code,
        other: r.other || '',
      });
    });
  });
  return countryList;
};

export const initCountry = async () => {
  const cdata = await fetchCountry();
  applyCountryFromApp(cdata);
};

/**
 * 写入国家字典（App Theme.payload.country 或接口响应）。
 * 仅非空对象时标记 countryFromApp，避免空 {} 阻断后续兜底请求。
 */
export const applyCountryFromApp = (cdata: unknown) => {
  orignCountryList = (cdata && typeof cdata === 'object' ? cdata : {}) as typeof orignCountryList;
  const countryList = mapCountryRows(orignCountryList);
  busConfigs.countryList = countryList;
  busConfigsMaps.countryList = {};
  eachGetMap('countryList', countryList);
  if (Object.keys(orignCountryList as object).length > 0) {
    countryFromApp = true;
  }
  bumpBusConfigsTick();
};

/**
 * 写入业务字典（App Theme.payload.busConfigs 或接口响应）。
 * 结构须与 GET /sys/dict/busConfigs 一致（code/enDesc/scDesc/tcDesc），非已 map 的 label/value。
 * 仅非空对象时标记 busConfigsFromApp，避免空 {} 阻断后续兜底请求。
 * App 偶发二次 stringify：string 时自动 JSON.parse。
 */
export const applyBusConfigsFromApp = (data: unknown) => {
  let raw: unknown = data;
  if (typeof raw === 'string') {
    try {
      raw = JSON.parse(raw);
    } catch {
      return;
    }
  }
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return;
  if (Object.keys(raw as object).length === 0) return;

  sourceData = raw as typeof sourceData;
  busConfigs.currencyType = getCurrencyType();
  initBusConfigsByLang();
  busConfigsInited = true;
  busConfigsFromApp = true;
  bumpBusConfigsTick();
};

/** Theme 空 payload 兜底：请求 /sys/dict/busConfigs 并灌入（等同 initCountry） */
export const initBusConfigsFromApi = async () => {
  const data: any = await fetchBusConfigs();
  applyBusConfigsFromApp(data);
};

export let busConfigsMaps = {
  // 身份证类型
  identityType: {},
  // 性别
  gender: {},
  // 学历
  education: {},
  // 受雇状态
  employmentStatus: {},
  // 行业
  industry: {},
  // 职位
  position: {},
  // 受雇年限
  employmentYear: {},
  // 收入来源
  incomeSource: {},
  // 资产类别
  assetCategory: {},
  // 总资产净值
  totalAssetNetWorth: {},
  // 每年总收入
  annualTotalIncome: {},
  // 物业情况
  housingProperty: {},
  // 财富来源 / 资金来源
  sourceOfFunds: {},
  // 资金转入方式
  fundTransferMethod: {},
  // 资金来源地
  sourceOfFundsLocation: {},
  // 投资目标
  investmentObjective: {},
  // 投资知识
  investmentKnowledge: {},
  // 投资经验类型
  investmentExperienceType: {},
  // 经验（年资）
  investmentExperience: {},
  // 基金账户
  fundAccountType: {},
  // 证券账户
  securitiesAccountType: {},
  // 交易权限
  tradePermission: {},
  // 香港银行列表
  hongKongBank: {},
  // 账户类型
  bankAccountType: {},
  // 开户方式
  openAccountMethod: {},
  // 币种
  depositCurrency: {},
  //
  electronicService: {},

  countryList: {},

  orderType: {},
};

const eachGetData = (type, lang?: string | null) => {
  const list = sourceData[type];
  if (!Array.isArray(list)) return [];
  const langKey = langMap[resolveBusLang(lang)];
  return list.map((item) => ({
    label: item?.[langKey] ?? '',
    value: item.code,
    other: item.other ?? '',
  }));
};

const eachGetMap = (type, data) => {
  if (!Array.isArray(data)) return;
  const map = (busConfigsMaps[type] ??= {});
  for (const item of data) {
    if (item == null || item.value == null) continue;
    map[item.value] = item.label;
  }
};

const getCurrencyType = (lang?: string | null) => {
  const labels: Record<BusLang, Record<string, string>> = {
    'zh-CN': { HKD: '港元', USD: '美元', CNY: '人民币' },
    'zh-TW': { HKD: '港元', USD: '美元', CNY: '人民幣' },
    en: { HKD: 'HKD', USD: 'USD', CNY: 'CNY' },
  };
  const L = labels[resolveBusLang(lang)];
  return [
    { value: 'HKD', label: L.HKD },
    { value: 'USD', label: L.USD },
    { value: 'CNY', label: L.CNY },
  ];
};

/** 按当前（或指定）语言重建字典 label / maps */
export const initBusConfigsByLang = (lang?: string | null) => {
  Object.keys(sourceData).forEach((key) => {
    const data = eachGetData(key, lang);
    busConfigs[key] = data;
    busConfigsMaps[key] = {};
    eachGetMap(key, data);
  });
};

/** 切语言后调用：字典 + 国家列表 + 币种 */
export const refreshBusConfigsByLang = (lang?: string | null) => {
  initBusConfigsByLang(lang);
  busConfigs.currencyType = getCurrencyType(lang);
  if (orignCountryList && Object.keys(orignCountryList as object).length > 0) {
    const countryList = mapCountryRows(orignCountryList, lang);
    busConfigs.countryList = countryList;
    busConfigsMaps.countryList = {};
    eachGetMap('countryList', countryList);
  }
  bumpBusConfigsTick();
};

/** 已成功拉过字典则跳过；并发调用复用同一 Promise，避免 country/busConfigs 重复请求 */
export const initBusConfigs = () => {
  if (busConfigsInited) {
    refreshBusConfigsByLang();
    return Promise.resolve({ data: busConfigs });
  }
  if (initBusConfigsPromise) return initBusConfigsPromise;

  initBusConfigsPromise = new Promise(async (resolve, reject) => {
    try {
      initBusConfigsByLang();

      // App：country / busConfigs 由 APP_TO_WEB_Theme 灌入（Theme 未到也不请求）；H5：接口拉取
      // Theme 空时分别走 initCountry / initBusConfigsFromApi 兜底
      const isAppWebView =
        typeof window !== 'undefined' && !!(window as any).ReactNativeWebView;
      const skipCountryFetch = countryFromApp || isAppWebView;
      if (!skipCountryFetch) {
        const cdata = await fetchCountry();
        orignCountryList = cdata;
        const countryList = mapCountryRows(cdata);
        busConfigsMaps.countryList = {};
        eachGetMap('countryList', countryList);
        busConfigs.countryList = countryList;
      }

      const skipBusFetch = busConfigsFromApp || isAppWebView;
      if (skipBusFetch) {
        busConfigs.currencyType = getCurrencyType();
        initBusConfigsByLang();
        if (busConfigsFromApp) {
          busConfigsInited = true;
        } else {
          // Theme 未到：不标记 inited，清空 promise，便于 Theme 灌入后再走短路
          initBusConfigsPromise = null;
        }
        resolve({ data: busConfigs });
        return;
      }

      try {
        const data: any = await fetchBusConfigs();
        sourceData = data;
        busConfigs.currencyType = getCurrencyType();
        initBusConfigsByLang();
        busConfigsInited = true;
        bumpBusConfigsTick();
        resolve({ data: busConfigs });
      } catch {
        // 与原先 finally 一致：失败不阻塞调用方；但不缓存，便于下次重试
        initBusConfigsByLang();
        initBusConfigsPromise = null;
        resolve({ data: busConfigs });
      }
    } catch (e) {
      initBusConfigsPromise = null;
      reject(e);
    }
  });

  return initBusConfigsPromise;
};
